import { useState } from 'react';
import toast from 'react-hot-toast';

interface ShareModalProps {
  formId: string;
  onClose: () => void;
}

const BASE_URL = import.meta.env.VITE_FORMS_CLIENT_URL || 'https://forms.getparallel.com';

export const ShareModal = ({ formId, onClose }: ShareModalProps) => {
  const formUrl = `${BASE_URL}?formId=${formId}`;
  const embedCode = `<script src="${BASE_URL}/embed.js" data-form-id="${formId}"></script>`;

  const iframeCode = `<iframe src="${formUrl}" width="100%" height="600" frameborder="0" style="border: none;"></iframe>`;

  const [copiedField, setCopiedField] = useState<'url' | 'embed' | 'iframe' | null>(null);

  const handleCopy = async (text: string, field: 'url' | 'embed' | 'iframe') => {
    await navigator.clipboard.writeText(text);
    setCopiedField(field);
    toast.success('Copied!');
    setTimeout(() => setCopiedField(null), 2000);
  };

  return (
    <div className="fixed inset-0 bg-gray-500 bg-opacity-75 flex items-center justify-center z-50">
      <div className="bg-white rounded-lg shadow-xl max-w-lg w-full mx-4">
        {/* Header */}
        <div className="px-6 py-4 border-b border-gray-200">
          <div className="flex justify-between items-center">
            <h3 className="text-lg font-medium text-gray-900">Share Form</h3>
            <button onClick={onClose} className="text-gray-400 hover:text-gray-500">
              <svg className="h-6 w-6" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M6 18L18 6M6 6l12 12" />
              </svg>
            </button>
          </div>
        </div>

        {/* Body */}
        <div className="px-6 py-4 space-y-6">
          {/* Direct Link */}
          <div>
            <label className="block text-sm font-medium text-gray-700 mb-2">Direct Link</label>
            <div className="flex items-center space-x-2">
              <input
                type="text"
                readOnly
                value={formUrl}
                className="flex-1 text-sm border border-gray-300 rounded-md px-3 py-2 bg-gray-50 text-gray-700"
              />
              <button
                onClick={() => handleCopy(formUrl, 'url')}
                className="px-3 py-2 text-sm font-medium text-gray-700 bg-white border border-gray-300 rounded-md hover:bg-gray-50 whitespace-nowrap"
              >
                {copiedField === 'url' ? 'Copied!' : 'Copy'}
              </button>
            </div>
          </div>

          {/* Script Embed */}
          <div>
            <label className="block text-sm font-medium text-gray-700 mb-1">Script Embed</label>
            <p className="text-xs text-gray-500 mb-2">Auto-sizes the form and captures UTM parameters.</p>
            <pre className="text-sm bg-gray-50 border border-gray-300 rounded-md p-3 overflow-x-auto whitespace-pre-wrap break-all text-gray-700">
              {embedCode}
            </pre>
            <button
              onClick={() => handleCopy(embedCode, 'embed')}
              className="mt-2 px-3 py-2 text-sm font-medium text-gray-700 bg-white border border-gray-300 rounded-md hover:bg-gray-50"
            >
              {copiedField === 'embed' ? 'Copied!' : 'Copy Script'}
            </button>
          </div>

          {/* Iframe Embed */}
          <div>
            <label className="block text-sm font-medium text-gray-700 mb-1">Iframe Embed</label>
            <p className="text-xs text-gray-500 mb-2">Simple embed with a fixed height.</p>
            <pre className="text-sm bg-gray-50 border border-gray-300 rounded-md p-3 overflow-x-auto whitespace-pre-wrap break-all text-gray-700">
              {iframeCode}
            </pre>
            <button
              onClick={() => handleCopy(iframeCode, 'iframe')}
              className="mt-2 px-3 py-2 text-sm font-medium text-gray-700 bg-white border border-gray-300 rounded-md hover:bg-gray-50"
            >
              {copiedField === 'iframe' ? 'Copied!' : 'Copy Iframe'}
            </button>
          </div>
        </div>

        {/* Footer */}
        <div className="px-6 py-4 bg-gray-50 rounded-b-lg flex justify-end">
          <button
            onClick={onClose}
            className="px-4 py-2 border border-gray-300 rounded-md shadow-sm text-sm font-medium text-gray-700 bg-white hover:bg-gray-50"
          >
            Done
          </button>
        </div>
      </div>
    </div>
  );
};
